import { useTranslation } from 'react-i18next'

import AddIcon from '@/assets/icons/add.svg?react'
import Button from '@/components/Button/Button'
import type { SelectOption } from '@/components/Select/Select'
import Filters, {
  type Filter,
  type FiltersProps
} from '@/components/common/Filters/Filters'
import { checkIfFilterSelected } from '@/utils/check-if-filters-selected'

import type {
  CurriculumsManagementFilters,
  CurriculumsManagementFiltersKey
} from '../constants/curriculums-management-filters'
import { curriculumsQueryFilterOptions } from '../queries/curriculumsFilters/curriculumsQueryFilterOptions'
import { educationLevelsQueryFilterOptions } from '../queries/curriculumsFilters/educationLevelsQueryFilterOptions'
import { getCurriculumAimOptions } from '../utils/get-curriculum-aim-options'

type CurriculumsManagementListFiltersProps = {
  filters: Omit<CurriculumsManagementFilters, 'page' | 'pageSize'>
  onFilterChange: (
    key: CurriculumsManagementFiltersKey,
    value?: SelectOption<string>[] | string[] | Date
  ) => void
} & Pick<FiltersProps, 'onClearAll'>

const CurriculumsManagementListFilters = (
  props: CurriculumsManagementListFiltersProps
) => {
  const { t } = useTranslation(['curriculumsManagement', 'common'])

  const filters: Filter[] = [
    {
      label: t('label.name'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'curriculum-filter',
        multiple: true,
        queryOptions: curriculumsQueryFilterOptions,
        value: props.filters.curriculum,
        placeholder: t('label.name'),
        onChange: value => props.onFilterChange('curriculum', value)
      }
    },
    {
      label: t('label.education-level'),
      variant: 'async-multiselect',
      filterProps: {
        id: 'education-level-filter',
        multiple: true,
        queryOptions: educationLevelsQueryFilterOptions,
        value: props.filters.educationLevel,
        placeholder: t('label.education-level'),
        onChange: value => props.onFilterChange('educationLevel', value)
      }
    },
    {
      label: t('label.aim'),
      variant: 'multiselect',
      filterProps: {
        id: 'aim-filter',
        multiple: true,
        options: getCurriculumAimOptions(t),
        value: props.filters.aim,
        placeholder: t('label.aim'),
        onChange: value => props.onFilterChange('aim', value)
      }
    }
  ]

  const isFilterSelected = checkIfFilterSelected(filters)

  return (
    <Filters
      filters={filters}
      disabledClearAllButton={!isFilterSelected}
      onClearAll={props.onClearAll}
      actionsRender={
        <Button asLink to="/curricula/add" icon={<AddIcon />}>
          {t('button.add-new-curriculum')}
        </Button>
      }
    />
  )
}

export default CurriculumsManagementListFilters
